import React from 'react';
import { FaCrosshairs, FaRegClock, FaCloudUploadAlt, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { checkLoggedIn } from '../functions/CookieHelper';
import './LogIn.css';

// Illustrative readouts for the landing hero. Decorative only — nothing here is
// fetched, and no survey data is available before sign-in. Rows name a
// `login.fields` key; the values are figures and are not translated.
const SURVEY_DATA = [
    ['depth', '125.40 m'],
    ['azimuth', '123.6°'],
    ['inclination', '-58.7°'],
    ['northing', '512342.12'],
    ['easting', '6987654.33'],
    ['elevation', '254.28']
];

const HOLE_POSITION = [
    ['depth', '125.40 m'],
    ['inclination', '-58.7°'],
    ['azimuth', '123.6°'],
    ['tvd', '66.12 m'],
    ['northing', '512342.12'],
    ['easting', '6987654.33']
];

// Each id names a `login.features` entry carrying its title and text.
const FEATURES = [
    { icon: FaCrosshairs, id: 'position' },
    { icon: FaRegClock, id: 'tracking' },
    { icon: FaCloudUploadAlt, id: 'repository' },
    { icon: FaUsers, id: 'industry' }
];

// Deviation trace: 0 m at x=40 through 250 m at x=330, 0° at y=30 through -4° at y=142.
const DEVIATION_PATH =
    'M40 31 C 78 33, 104 44, 132 64 S 190 116, 232 132 S 300 143, 330 145';

// Gamma trace: a fixed sum of sines, so it is a plausible wiggle that never
// changes between renders (no Math.random — that would jitter on every repaint).
const GAMMA_PATH = (() => {
    let d = '';
    for (let i = 0; i <= 170; i++) {
        const t = i * 0.9;
        const x = 150 + 17 * Math.sin(t) + 11 * Math.sin(t * 2.3 + 1.1) + 6 * Math.sin(t * 5.7 + 0.4);
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${30 + i}`;
    }
    return d;
})();

const Panel = ({ label, rows }) => {
    const { t } = useTranslation();
    return (
        <div className="login-panel">
            <div className="login-overlay-label">{label}</div>
            {rows.map(([field, value]) => (
                <div className="login-panel-row" key={field}>
                    <span>{t(`login.fields.${field}`)}</span>
                    <span>{value}</span>
                </div>
            ))}
        </div>
    );
};

const DeviationChart = () => (
    <svg className="login-chart" width="360" height="175" viewBox="0 0 360 175" aria-hidden="true">
        {[0, 1, 2, 3, 4].map(i => (
            <line key={i} className="grid" x1="40" y1={30 + i * 28} x2="330" y2={30 + i * 28} />
        ))}
        {[0, 1, 2, 3, 4, 5].map(i => (
            <line key={i} className="grid" x1={40 + i * 58} y1="30" x2={40 + i * 58} y2="142" />
        ))}
        <line className="axis" x1="40" y1="30" x2="40" y2="142" />
        <line className="axis" x1="40" y1="142" x2="330" y2="142" />
        <path className="trace" d={DEVIATION_PATH} />
        {[0, 1, 2, 3, 4].map(i => (
            <text key={i} x="34" y={34 + i * 28} textAnchor="end">{i === 0 ? '0°' : `-${i}°`}</text>
        ))}
        {[0, 50, 100, 150, 200, 250].map((m, i) => (
            <text key={m} x={40 + i * 58} y="158" textAnchor="middle">{m}m</text>
        ))}
    </svg>
);

const GammaChart = () => (
    <svg className="login-chart" width="230" height="215" viewBox="60 10 230 215" aria-hidden="true">
        <line className="axis" x1="100" y1="25" x2="100" y2="205" />
        {[0, 50, 100, 150, 200].map((v, i) => (
            <React.Fragment key={v}>
                <line className="grid" x1="100" y1={30 + i * 42.5} x2="240" y2={30 + i * 42.5} />
                <text x="94" y={34 + i * 42.5} textAnchor="end">{v}</text>
            </React.Fragment>
        ))}
        <path className="trace" d={GAMMA_PATH} />
    </svg>
);

export const LogIn = () => {
    const { t } = useTranslation();

    //get the returnPage from the query string
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const returnPage = urlParams.get('returnPage');

    const [loggedIn, setLoggedIn] = React.useState(checkLoggedIn());
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [authorising, setAuthorising] = React.useState(false);

    const handleUsernameChange = (event) => {
        setUsername(event.target.value);
    }

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        if (username === '' || password === '') {
            alert(t('login.missingCredentials'));
            return;
        }
        const loginParams = {
            username: username,
            password: password
        };
        setAuthorising(true);
        fetch('api/identity/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(loginParams)
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    console.log('Success:', JSON.stringify(data, null, 2));
                    setLoggedIn(true);
                    document.location.href = returnPage ? returnPage : '/overview';

                }
                else {
                    console.log('Failure:', JSON.stringify(data, null, 2));
                    alert(data.message);
                }

                setAuthorising(false);
                setPassword('');
            })
            .catch((error) => {
                console.error('Error:', error);
                setAuthorising(false);
                setPassword('');
            }
            );

    }

    const logInForm = (
        <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
                <label htmlFor="name">{t('login.name')}</label>
                <input type="text" className="form-control" id="name" name="name" autoComplete="username" value={username} onChange={handleUsernameChange} />
            </div>
            <div className="form-group mb-3">
                <label htmlFor="password">{t('login.password')}</label>
                <input type="password" className="form-control" id="password" name="password" autoComplete="current-password" value={password} onChange={handlePasswordChange} />
            </div>
            <button type="submit" className="btn btn-primary">{t('login.submit')}</button>
        </form>
    );

    return (
        <div
            className="login-hero"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/login-hero.jpg)` }}
        >
            <div className="login-hero-body">
                <div className="login-overlay login-overlay-tl">
                    <div className="login-overlay-label">{t('login.holeDeviation')}</div>
                    <DeviationChart />
                </div>

                <div className="login-overlay login-overlay-bl">
                    <div className="login-overlay-label">{t('login.gammaApi')}</div>
                    <GammaChart />
                </div>

                <div className="login-overlay login-overlay-tr">
                    <Panel label={t('login.surveyData')} rows={SURVEY_DATA} />
                </div>

                <div className="login-overlay login-overlay-br">
                    <Panel label={t('login.holePosition')} rows={HOLE_POSITION} />
                </div>

                <div className="login-hero-centre">
                    <h1 className="login-hero-title">{t('common.welcome')}</h1>
                    {loggedIn ? (
                        <h2 className="login-hero-subtitle">{t('login.loggedIn')}</h2>
                    ) : authorising ? (
                        <h2 className="login-hero-subtitle">{t('login.loggingIn')}</h2>
                    ) : (
                        <>
                            <h2 className="login-hero-subtitle">{t('login.logIn')}</h2>
                            {logInForm}
                        </>
                    )}
                </div>
            </div>

            <div className="login-features">
                {FEATURES.map(({ icon: Icon, id }) => (
                    <div className="login-feature" key={id}>
                        <Icon className="login-feature-icon" aria-hidden="true" />
                        <div>
                            <h3>{t(`login.features.${id}.title`)}</h3>
                            <p>{t(`login.features.${id}.text`)}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
